import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import BaseModal, { ModalFooter } from "../base/BaseModal";
import CustomSelect from "../base/CustomSelect";
import { 
  validateCreateWorkScheduleForm, 
  validateUpdateWorkScheduleForm,
  canEditWorkSchedule,
  RECEPTION_SESSION_OPTIONS,
} from "../../validator/workScheduleValidator";
import dayjs from "dayjs";
import { hourFormat } from "../../utils/dateUtils";
import { showToast } from "../../utils/toastNotification";
import VietnameseDatePicker from "../documents/VietnameseDatePicker";
import { USER_API } from "../../apis/user";

const WorkScheduleModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  mode = "create",
}) => {
  const [formData, setFormData] = useState({
    diaDiem: "Phòng tiếp dân",
    tenCanBo: "",
    caTiepDan: "07:30 - 11:00",
    batDau: "07:30",
    ketThuc: "11:00",
    ngayTiepDan: dayjs().add(1, "day").format("YYYY-MM-DD"),
    ghiChu: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalDate, setOriginalDate] = useState(null);
  const [originalStartTime, setOriginalStartTime] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const canEdit = mode === "create" || canEditWorkSchedule(originalDate, originalStartTime);

  useEffect(() => {
    if (isOpen) {
      const fetchOfficers = async () => {
        setLoadingUsers(true);
        try {
          const res = await USER_API.getAllUsersWithPagination({
            page: 1,
            size: 100,
            isActive: true,
            permission: "LTD",
          });
          const list = Array.isArray(res) ? res : res?.content || res?.data || [];
          setUsers(list);
        } catch (error) {
          console.error("Error fetching officers:", error);
        } finally {
          setLoadingUsers(false);
        }
      };
      fetchOfficers();
    }
  }, [isOpen]);

  const officerOptions = useMemo(() => {
    const uniqueNames = new Set();
    const list = [];

    users.forEach((u) => {
      const name = u.fullName?.trim();
      if (name && !uniqueNames.has(name)) {
        uniqueNames.add(name);
        list.push({
          value: name,
          label: name,
        });
      }
    });

    if (formData.tenCanBo && !uniqueNames.has(formData.tenCanBo)) {
      list.unshift({
        value: formData.tenCanBo,
        label: formData.tenCanBo,
      });
    }

    return list;
  }, [users, formData.tenCanBo]);

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && initialData) {
        const dateValue = initialData.ngay_tiep_dan || initialData.ngayTiepDan;
        const formattedDate = dateValue
          ? dayjs(dateValue).format("YYYY-MM-DD")
          : "";
        const { start, end } = hourFormat(initialData.thoi_gian || "");

        setOriginalDate(dateValue);
        setOriginalStartTime(start);
        const selectedSession = RECEPTION_SESSION_OPTIONS.find(
          (session) => session.batDau === (initialData.batDau || start) && session.ketThuc === (initialData.ketThuc || end),
        );

        setFormData({
          diaDiem: initialData.dia_diem || initialData.diaDiem || "Phòng tiếp dân",
          tenCanBo: initialData.ten_can_bo || initialData.tenCanBo || "",
          caTiepDan: selectedSession?.value || "",
          batDau: initialData.batDau || start || "",
          ketThuc: initialData.ketThuc || end || "",
          ngayTiepDan: formattedDate,
          ghiChu: initialData.ghi_chu || initialData.ghiChu || "",
        });

        if (!canEditWorkSchedule(dateValue, start)) {
          showToast.warning("Lịch công tác này đã bắt đầu hoặc đã qua, không thể chỉnh sửa!");
        }
      } else {
        setOriginalDate(null);
        setOriginalStartTime(null);
        resetForm();
      }
      setErrors({});
    }
  }, [initialData, mode, isOpen]);

  const getMinDateForInput = () => {
    return dayjs().format("YYYY-MM-DD");
  };

  const resetForm = () => {
    setFormData({
      diaDiem: "Phòng tiếp dân",
      tenCanBo: "",
      caTiepDan: "07:30 - 11:00",
      batDau: "07:30",
      ketThuc: "11:00",
      ngayTiepDan: dayjs().add(1, "day").format("YYYY-MM-DD"),
      ghiChu: "",
    });
    setErrors({});
    setIsSubmitting(false);
  };

  const validateForm = async () => {
    if (mode === "create") {
      const { isValid, errors: validationErrors } = await validateCreateWorkScheduleForm(formData);
      setErrors(validationErrors || {});
      if (!isValid) {
        showToast.error('Vui lòng kiểm tra lại các trường bắt buộc!');
      }
      return isValid;
    } else {
      const { isValid, errors: validationErrors } = await validateUpdateWorkScheduleForm(
        formData, 
        originalDate, 
        originalStartTime
      );
      setErrors(validationErrors || {});
      if (!isValid) {
        showToast.error('Vui lòng kiểm tra lại các trường bắt buộc!');
      }
      return isValid;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      if (mode === "edit" && !canEdit) {
        showToast.error("Không thể chỉnh sửa lịch công tác đã qua ngày hiện tại!");
        setIsSubmitting(false);
        return;
      }

      const isValid = await validateForm();

      if (!isValid) {
        setIsSubmitting(false);
        return;
      }

      const { caTiepDan, ...submitData } = formData;
      submitData.diaDiem = formData.diaDiem?.trim() || "Phòng tiếp dân";

      const success = await onSubmit(submitData, mode);

      if (success) {
        resetForm();
        onClose();
      } else {
        setIsSubmitting(false);
      }
    } catch (error) {
      showToast.error(error?.message || "Đã có lỗi xảy ra. Vui lòng thử lại.");
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    const newErrors = { ...errors };
    if (newErrors[field]) {
      delete newErrors[field];
    }

    if (field === "ngayTiepDan" && value) {
      delete newErrors.ngayTiepDan;
      delete newErrors.batDau;
      delete newErrors.ketThuc;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const selectedDate = new Date(value);
      selectedDate.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        newErrors.ngayTiepDan = "Không thể chọn ngày trong quá khứ";
      }
    }

    setErrors(newErrors);
  };

  const updateSession = (sessionValue) => {
    const session = RECEPTION_SESSION_OPTIONS.find(({ value }) => value === sessionValue);
    setFormData((prev) => ({
      ...prev,
      caTiepDan: sessionValue,
      batDau: session?.batDau || "",
      ketThuc: session?.ketThuc || "",
    }));
    setErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors.caTiepDan;
      delete nextErrors.batDau;
      delete nextErrors.ketThuc;
      return nextErrors;
    });
  };

  const modalTitle =
    mode === "create" ? "Thêm lịch tiếp công dân" : "Chỉnh sửa lịch tiếp công dân";
  const submitText = mode === "create" ? "Tạo lịch" : "Cập nhật";

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={modalTitle}
      subtitle="Nhập thông tin ngày, cán bộ và ca tiếp công dân"
      size="md"
      className="max-w-xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          onSubmit={handleSubmit}
          submitText={submitText}
          submitDisabled={isSubmitting || (mode === "edit" && !canEdit)}
          submitLoading={isSubmitting}
        />
      }
    >
      <div className="space-y-5 min-h-[320px]">
        <div>
          <label className="block text-sm required-label font-medium text-gray-700 mb-1.5">
            Ngày tiếp dân
          </label>
          <VietnameseDatePicker
            value={formData.ngayTiepDan}
            onChange={(isoVal) => updateField("ngayTiepDan", isoVal)}
            minDate={getMinDateForInput()}
            disabled={mode === "edit" && !canEdit}
            error={!!errors.ngayTiepDan}
            placeholder="dd/MM/yyyy"
          />
          {errors.ngayTiepDan && (
            <p className="mt-1 text-sm text-red-500">{errors.ngayTiepDan}</p>
          )}
          {mode === "edit" && !canEdit && (
            <p className="mt-1 text-sm text-orange-500">
              * Lịch công tác đã qua không thể chỉnh sửa ngày
            </p>
          )}
        </div>

        {/* Tên cán bộ tiếp dân */}
        <div>
          <label className="block text-sm required-label font-medium text-gray-700 mb-1.5">
            Tên cán bộ tiếp dân
          </label>
          <CustomSelect
            value={formData.tenCanBo}
            onChange={(val) => {
              const selectedVal = typeof val === 'object' ? val?.target?.value : val;
              updateField("tenCanBo", selectedVal);
            }}
            options={officerOptions}
            placeholder={loadingUsers ? "-- Đang tải danh sách cán bộ... --" : "-- Chọn cán bộ tiếp dân --"}
            disabled={mode === "edit" && !canEdit}
            hasError={Boolean(errors.tenCanBo)}
            searchable={true}
          />
          {errors.tenCanBo && (
            <p className="mt-1 text-sm text-red-500">{errors.tenCanBo}</p>
          )}
        </div>

        <div>
          <label className="block required-label text-sm font-medium text-gray-700 mb-1">
            Ca tiếp dân
          </label>
          <select
            value={formData.caTiepDan}
            onChange={(event) => updateSession(event.target.value)}
            disabled={mode === "edit" && !canEdit}
            className={`w-full h-10 px-3 bg-gray-50 border rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.caTiepDan ? "border-red-500" : "border-gray-300"
            } ${mode === "edit" && !canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <option value="">Chọn ca tiếp dân</option>
            {RECEPTION_SESSION_OPTIONS.map((session) => (
              <option key={session.value} value={session.value}>
                {session.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Chỉ áp dụng hai ca cố định theo quy định tiếp dân.
          </p>
          {errors.caTiepDan && (
            <p className="mt-1 text-sm text-red-500">{errors.caTiepDan}</p>
          )}
        </div>

        {/* Ghi chú */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Ghi chú
            </label>
            <span className={`text-xs ${formData.ghiChu?.length >= 255 ? "text-amber-600 font-semibold" : "text-gray-400"}`}>
              {formData.ghiChu?.length || 0}/255
            </span>
          </div>
          <textarea
            rows={2}
            placeholder="Nhập ghi chú nếu có..."
            value={formData.ghiChu}
            onChange={(e) => updateField("ghiChu", e.target.value)}
            disabled={mode === "edit" && !canEdit}
            maxLength={255}
            className={`w-full px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-900 focus:bg-white focus:outline-none focus:ring-2 resize-none ${
              errors.ghiChu
                ? "border-red-500 focus:ring-red-500"
                : formData.ghiChu?.length >= 255
                ? "border-amber-400 focus:ring-amber-400"
                : "border-gray-300 focus:ring-blue-500"
            } ${mode === "edit" && !canEdit ? "opacity-50 cursor-not-allowed" : ""}`}
          />
          <div className="flex justify-between items-center text-xs mt-1">
            {errors.ghiChu ? (
              <p className="text-red-500">{errors.ghiChu}</p>
            ) : formData.ghiChu?.length >= 255 ? (
              <p className="text-amber-600 font-medium">Đã đạt giới hạn tối đa 255 ký tự</p>
            ) : null}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

WorkScheduleModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  mode: PropTypes.oneOf(["create", "edit"]),
};

export default WorkScheduleModal;
